import axios, { AxiosInstance } from 'axios';
import { getEnvVar } from '../config/env';
import type { LoginCredentials, User, ApiResponse, AuthResponse } from '../types';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

class AuthService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearAuthToken();
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('admin_auth_token') || 
           document.cookie
             .split('; ')
             .find(row => row.startsWith('auth_token='))
             ?.split('=')[1] || null;
  }

  private setAuthToken(token: string): void {
    localStorage.setItem('admin_auth_token', token);
    // Also set in cookie for cross-subdomain access
    document.cookie = `auth_token=${token}; path=/; max-age=86400; SameSite=Lax`;
  }

  private clearAuthToken(): void {
    localStorage.removeItem('admin_auth_token');
    document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  // Login
  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
      const response = await this.api.post<ApiResponse<AuthResponse>>('/admin/auth/login', credentials, {
        timeout: 30000,
      });
      console.log('AuthService: Login response received', response.data);
      
      if (response.data.success && response.data.data?.token) {
        this.setAuthToken(response.data.data.token);
        // Store user info
        if (response.data.data.user) {
          localStorage.setItem('admin_user', JSON.stringify(response.data.data.user));
        }
        console.log('AuthService: Token stored successfully');
      } else {
        console.warn('AuthService: Login response missing token', response.data);
      }
      
      return response.data;
    } catch (error: any) {
      console.error('AuthService: Login error', error);
      console.error('AuthService: Error response', error.response?.data);
      console.error('AuthService: Error status', error.response?.status);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          error.message || 
                          'Login failed';
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await this.api.post('/admin/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearAuthToken();
      localStorage.removeItem('admin_user');
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User> {
    try {
      const response = await this.api.get<ApiResponse<User>>('/admin/auth/me');
      if (response.data.success && response.data.data) {
        localStorage.setItem('admin_user', JSON.stringify(response.data.data));
        return response.data.data;
      }
      throw new Error('Failed to get user');
    } catch (error) {
      // Try to get from localStorage as fallback
      const storedUser = localStorage.getItem('admin_user');
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch (e) {
          throw new Error('Failed to get user');
        }
      }
      throw new Error('Failed to get user');
    }
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Check role
  hasRole(role: 'user' | 'workspace_admin' | 'super_admin'): boolean {
    const user = this.getCurrentUserSync();
    return user?.role === role;
  }

  // Check permission
  hasPermission(permission: string): boolean {
    const user = this.getCurrentUserSync();
    if (!user) return false;
    
    // Super admin has all permissions
    if (user.role === 'super_admin') return true;
    
    // Check user permissions
    return user.permissions?.includes(permission) || false;
  }

  // Check if super admin
  isSuperAdmin(): boolean {
    return this.hasRole('super_admin');
  }

  // Check if workspace admin
  isWorkspaceAdmin(): boolean {
    return this.hasRole('workspace_admin');
  }

  // Get current user from localStorage (synchronous)
  private getCurrentUserSync(): User | null {
    try {
      const storedUser = localStorage.getItem('admin_user');
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    return null;
  }
}

export const authService = new AuthService();
export default authService;


